import React, { useMemo, useState } from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import { getChartTokens } from '../../theme/chartTheme';
import { contentBorders } from '../../theme/createContentTheme';

export interface CompositionRow {
  id?: string;
  label: string;
  value: number;
  /** Optional secondary caption (e.g. emission-factor version). */
  meta?: React.ReactNode;
  color?: string;
}

export interface CompositionListProps {
  rows: CompositionRow[];
  /** Formats a row value (figure only). */
  formatValue: (value: number) => string;
  unit?: string;
  /** Rows shown before "Show all". */
  maxRows?: number;
  /** Colour swatches (when the list doubles as a donut legend). */
  swatches?: boolean;
  /** Override the denominator for shares (defaults to the sum of rows). */
  total?: number;
  dense?: boolean;
  ariaLabel?: string;
}

/**
 * Ranked composition: label, value + unit, share of total and a thin bar
 * relative to the largest row. Real text (not SVG), so it mirrors in RTL and
 * stays readable on mobile — preferred over donuts with many segments.
 */
const CompositionList: React.FC<CompositionListProps> = ({
  rows,
  formatValue,
  unit,
  maxRows = 8,
  swatches = false,
  total,
  dense = false,
  ariaLabel,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const fmt = useNumberFormat();
  const tokens = useMemo(() => getChartTokens(theme), [theme]);
  const borders = contentBorders(theme);
  const [expanded, setExpanded] = useState(false);

  const sorted = useMemo(
    () =>
      rows
        .filter((r) => Number.isFinite(r.value))
        .map((r, i) => ({ ...r, color: r.color ?? tokens.seriesColor(i) }))
        .sort((a, b) => b.value - a.value),
    [rows, tokens],
  );
  const sum = total ?? sorted.reduce((acc, r) => acc + Math.max(r.value, 0), 0);
  const max = sorted.reduce((acc, r) => Math.max(acc, r.value), 0);
  const visible = expanded ? sorted : sorted.slice(0, maxRows);

  return (
    <Box>
      <Box component="ol" aria-label={ariaLabel} sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {visible.map((row) => {
          const share = sum > 0 ? row.value / sum : null;
          const width = max > 0 ? Math.max((row.value / max) * 100, row.value > 0 ? 1.5 : 0) : 0;
          return (
            <Box
              component="li"
              key={row.id ?? row.label}
              sx={{
                py: dense ? 1 : 1.25,
                borderBottom: `1px solid ${borders.subtle}`,
                '&:last-of-type': { borderBottom: 0 },
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) auto auto',
                  alignItems: 'baseline',
                  columnGap: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  {swatches ? (
                    <Box
                      aria-hidden
                      sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: row.color, flexShrink: 0 }}
                    />
                  ) : null}
                  <Typography variant="body2" noWrap title={row.label} sx={{ color: 'text.primary', minWidth: 0 }}>
                    {row.label}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', textAlign: 'end', whiteSpace: 'nowrap' }}
                >
                  <bdi>{formatValue(row.value)}</bdi>
                  {unit ? (
                    <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400, ml: 0.5 }}>
                      {unit}
                    </Box>
                  ) : null}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontVariantNumeric: 'tabular-nums',
                    minWidth: 44,
                    textAlign: 'end',
                  }}
                >
                  <bdi>{fmt.share(share)}</bdi>
                </Typography>
              </Box>
              <Box
                aria-hidden
                sx={{
                  mt: 0.75,
                  height: 4,
                  borderRadius: '2px',
                  bgcolor: borders.subtle,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: `${width}%`,
                    height: '100%',
                    bgcolor: swatches ? row.color : 'primary.main',
                    opacity: swatches ? 1 : 0.85,
                    transition: 'width 400ms ease',
                    animation: 'esg-grow-width 800ms ease-out',
                  }}
                />
              </Box>
              {row.meta ? (
                <Typography variant="caption" component="p" sx={{ mt: 0.5, color: 'text.secondary' }}>
                  {row.meta}
                </Typography>
              ) : null}
            </Box>
          );
        })}
      </Box>
      {sorted.length > maxRows ? (
        <Button
          size="small"
          color="inherit"
          onClick={() => setExpanded((v) => !v)}
          sx={{ mt: 1, color: 'text.secondary', px: 1 }}
        >
          {expanded ? t('common.showLess') : t('common.showAll', { count: sorted.length })}
        </Button>
      ) : null}
    </Box>
  );
};

export default CompositionList;
